import { IPaymentMakePaymentPayload } from "@/api_framework/api_modals/payment";
import { usableRewardCredit } from "@/app/utils/rewardCredit";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import { usePaymentUserRecordPaymentAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserRecordPaymentAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ROUTES } from "@/app/routes";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  setPaymentDetails?: (value: any) => void;
  paymentDetails?: any;
  formHook: any;
  isSnapmintValue?: any;
  paidSnapmintInvoices?: any;
  totalRemaing: any;
  onPaymentSuccess?: () => void;
  rewardCreditBalance?: number;
};

const PaymentConfirmationModal: React.FC<Props> = ({
  open,
  setOpen,
  formHook,
  paidSnapmintInvoices,
  isSnapmintValue,
  totalRemaing,
  onPaymentSuccess,
  rewardCreditBalance = 0,
}) => {
  const cancelButtonRef = useRef(null);
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;

  const [loader, setLoader] = useState<boolean>(false);
  const { execute: createPaymentRecord } = usePaymentUserRecordPaymentAPI();
  const fetchCustomerBookingsAPI = useUserBookingsListAPI();

  const { routeState } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { customerBookings } = routeState.state as ISlotsState;

  const getBookingTimestamp = (booking: any) => {
    const bookingDateTime = booking?.booking_start_date_time;
    const fallbackBookingDateTime = booking?.booking_start_date
      ? `${booking.booking_start_date}T${booking?.booking_start_time || "00:00:00"}`
      : null;
    return new Date(bookingDateTime || fallbackBookingDateTime || 0).getTime();
  };

  const isBookingPresent =
    [...(customerBookings?.data ?? [])]
      .filter((booking) => !["Cancelled", "Failed"].includes(booking?.booking_status))
      .sort((a, b) => getBookingTimestamp(b) - getBookingTimestamp(a))[0] ?? null;

  // RBAC: CART_BOOKING_MAPPED_CLINIC_BYPASS — legacy roles: clinic_ops, clinic_ops_bluecity, clinic_ops_doggyworld, super_admin
  const isClinicOpsUser = useFeatureAccess("CART_BOOKING_MAPPED_CLINIC_BYPASS");

  const isBookingPresentInMappedClinic = () => {
    if (isClinicOpsUser) return true;
    if (!mappedClinicId || !isBookingPresent?.clinic_id) return false;
    return isBookingPresent.clinic_id === mappedClinicId;
  };


  const isValidAmount = (amount) => parseFloat(amount || "0") !== 0;

  // Normalize and filter each array
  const offline_payments = formHook?.offline_payments?.filter(item => isValidAmount(item.amount))
    .map(item => ({
      amount: parseFloat(item.amount),
      payment_mode: item.payment_mode,
      order_id: null,
      rrn: item.bank_ref_no || null
    }));

  const onlinePayments = formHook?.payments?.filter(item => isValidAmount(item.amount))
    .map(item => ({
      amount: parseFloat(item.amount),
      payment_mode: item.payment_mode,
      order_id: item?.order_id
    }));

  // Merge both arrays with duplicates allowed
  const allPaymentMethod = [...offline_payments, ...onlinePayments];



  // used_amount includes Reward credit; take that part out first, capped by eligible clinic invoices.
  const splitUsedAmount = (payingInvoices = formHook?.invoices ?? []) => {
    const used = parseFloat(formHook?.used_amount) || 0;
    const eligible = (payingInvoices ?? []).map((inv: any) => ({
      ...(formHook?.invoices ?? []).find((item: any) => item.invoice_id === inv.invoice_id),
      amount: inv.amount ?? inv.amount_to_be_paid,
    }));
    const rewardCredit = Math.min(used, usableRewardCredit(rewardCreditBalance, eligible, "amount"));
    return { rewardCredit, walletAmount: Number((used - rewardCredit).toFixed(2)) };
  };

  const handelClick = () => {
    const data = formHook;

    setLoader(true);
    const invoice = data.invoices
      ?.filter((item) => item.invoice_id)
      .map((item) => ({
        invoice_id: item.invoice_id,
        amount: parseFloat(item.amount_to_be_paid),
      }));

    const updatedInvoices = invoice?.map((inv) => {
      const snapmintEligibleInvoices = paidSnapmintInvoices?.order_value_per_invoice || {};
      const isSnapmint = inv.invoice_id in snapmintEligibleInvoices;
      const snapmintValue = snapmintEligibleInvoices[inv.invoice_id] || 0;

      return {
        ...inv,
        amount: isSnapmint ? inv.amount - snapmintValue : inv.amount,
      };
    });

    const filteredInvoices = updatedInvoices?.filter((invoice) => invoice?.amount !== 0);
    const { rewardCredit, walletAmount } = splitUsedAmount(filteredInvoices);

    const constructedBody: IPaymentMakePaymentPayload = {
      user_id: customerId,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      invoices: filteredInvoices,
      wallet_amount_consume: walletAmount,
      ...(rewardCredit > 0 ? { reward_credit_consume: rewardCredit } : {}),
      payments_details_list: allPaymentMethod,
    };
    createPaymentRecord(constructedBody).then(({ status }) => {
      if (status) {
        setLoader(false);
        setOpen(false);
        if (onPaymentSuccess) {
          onPaymentSuccess();
        } else {
          navigate(`/app/customer/profile/customer-details/invoice/${customerId}/${petId}`);
        }
      } else {
        setLoader(false);
      }
    });
  };



  const sortedPayments = allPaymentMethod.sort((a, b) => {
    if (a.order_id && !b.order_id) return -1;
    if (!a.order_id && b.order_id) return 1;
    return 0;
  });


  const calculateTotal = () => {
    const totalAmount = sortedPayments?.reduce((sum, entry) => sum + entry.amount, 0) || 0;
    const total = totalAmount + (parseFloat(formHook?.used_amount) || 0);
    return Number(total.toFixed(2));
  };

  useEffect(() => {
    if (customerId && petId) {
      fetchCustomerBookingsAPI(customerId, petId, {
        limit: 10,
        page: 1,
        append: false,
        filter_by: "all",
      });
    }
  }, [customerId, petId]);
  const canConfirmPayment = isBookingPresentInMappedClinic();

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left p-3 overflow-visible  shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-3xl">
                {/* {filteredPaymentsItems?.length === 0 ? (
                  <div className="bg-white  pt-5 pb-4 sm:p-6 sm:pb-4 rounded-lg my-10 px-10 ">
                    <p className="text-sm">
                      No dues are pending. please see{" "}
                      <span className="text-blue-400 font-medium cursor-pointer" onClick={() => redirectToInvoice()}>
                        Invoice
                      </span>
                    </p>

                    <div className=" flex justify-end">
                      {" "}
                      <button
                        className="rounded-md bg-[#193A6A] font-bold px-10 py-2 text-sm text-white "
                        onClick={() => redirectToInvoice()}
                      >
                        View Invoice
                      </button>
                    </div>
                  </div>
                ) : ( */}

                {!canConfirmPayment && <p className="text-red-700 text-sm p-2">The last booking clinic and your mapped clinic do not match. Please update it and continue.</p>}


                <div className="bg-white px-4  pb-4 sm:p-6 sm:pb-4 rounded-lg">
                  <div className="flex justify-between items-center mt-2">
                    <h2 className="text-base font-semibold">
                      {" "}
                      Are you sure you have collected payment's with below mentioned details?
                    </h2>
                    <XMarkIcon className="w-5 h-5 font-bold cursor-pointer" onClick={() => setOpen(false)} />
                  </div>
                  <div className="p-4 bg-white  rounded-lg">
                    <h3 className="text-sm font-semibold ">Payment Method</h3>
                    <div className="space-y-1">

                      {parseFloat(formHook?.used_amount) > 0 && (
                        <div className=" flex justify-between items-center  p-3 rounded-md ">
                          <span>

                            <span>1.</span>

                            &nbsp;
                            <span className="font-medium text-sm text-green-700">Wallet:</span>{" "}
                          </span>
                          <span className="text-gray-700 font-semibold">
                            ₹{formHook?.used_amount || 0}
                            {splitUsedAmount().rewardCredit > 0 && (
                              <span className="ml-1 text-xs font-normal text-green-700">
                                (incl. ₹{splitUsedAmount().rewardCredit} Reward credit)
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {sortedPayments?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center  p-3 rounded-md">
                          <span>
                            {parseFloat(formHook?.used_amount) > 0 ? <span>{index + 2}.</span> : <span>{index + 1}.</span>}&nbsp;
                            <span className="font-medium text-sm text-green-700">{item?.payment_mode} {!item?.order_id && <span className="text-red-400">(Offline)</span>}</span>
                          </span>

                          <span className="text-gray-700 font-semibold">₹{item?.amount}</span>
                        </div>
                      ))}
                    </div>

                  </div>
                  <hr className=" border-gray-300" />

                  <div className="p-4 ">
                    <div className="flex justify-between items-center text-sm text-gray-700">
                      <span className="font-medium"></span>
                      <span className="text-base font-semibold text-gray-900">
                        Total : ₹{calculateTotal()}
                      </span>
                    </div>
                    <div className="mt-2 flex justify-between items-center text-sm text-gray-700">
                      <span className="font-medium"></span>
                      <span
                        className={`text-base font-semibold ${totalRemaing < 0 ? "text-green-600" : "text-red-600"
                          }`}
                      >
                        {totalRemaing < 0 ? "Advance Collection" : "Overdue"} :{` ₹${Math.abs(totalRemaing)}`}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 mt-6">
                    <button
                      className="rounded-md bg-gray-400 font-bold px-10 py-2 text-sm text-white "
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      No
                    </button>
                    <button
                      className={`rounded-md font-bold px-10 py-2 text-sm text-white bg-[#193A6A]`}
                      onClick={() => {
                        handelClick();
                      }}
                    >
                      {loader ? (
                        <div className="">
                          {" "}
                          <CircleSpinnerLoader h="20" w="20" />
                        </div>
                      ) : (
                        "Yes"
                      )}
                    </button>
                  </div>
                </div>
                {/* )} */}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default PaymentConfirmationModal;
